// The one TS driver of the kit's seam (kit:Decision/one-json-seam, kit:Interface/seam): every core (the kit's sync
// engine, truss, auth, rigid) is `call(input) → reply` + `takeIo() → [io]`, JSON text both ways, over any binding
// (wasm, the C ABI through bun:ffi). The driver calls, drains and hands each io item to the host's handler for its
// name. A handler that returns a promise is a capability (sql, fetch, crypto, …): its value goes back as
// `{in: "answer", id, ok}`, a throw as `{in: "answer", id, error: {code, message}}`. A handler that returns nothing
// answers later by itself (a socket's frames, a timer) or needs no answer (view, effect). A server core's request
// (`request(input)`) gets the host's `id` and resolves with its `respond {req, …}` item. Holds no rule.
// No imports: a Worker that bundles truss or auth does not pull the kit's wasm.

/** A binding's surface: the wasm classes and the C ABI wrapper all are one. */
export interface Core {
	call(input: string): string;
	takeIo(): string;
}

/** One io item: `io` names it; `id` correlates its answer (absent: no answer). */
export type IoItem = { io: string; id?: number; [k: string]: unknown };
/** A finished request of a server core. */
export type Respond = { io: "respond"; req: number; error?: { code: string; message: string }; [k: string]: unknown };
export type Handler = (item: any) => Promise<unknown> | void;
export type Handlers = Record<string, Handler>;

/** A refused io answer: `code` travels to the core (e.g. `constraint`, `unauthorized`). */
export class IoError extends Error {
	constructor(readonly code: string, message: string) {
		super(message);
		this.name = "IoError";
	}
}

/** 32 bytes of crypto randomness, base64url: the `entropy` a server core's request carries (ids, secrets). */
export function entropy(): string {
	return btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32)))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export class Driver {
	private draining = false;
	private closed = false;
	private next = 0;
	private waiting = new Map<number, (r: Respond) => void>();

	constructor(
		private core: Core,
		private handlers: Handlers,
		private onWarn: (message: string) => void = (m) => console.warn(m),
	) {}

	/** One input. `before` sees the reply before the drain (register a callback the drain may already fire). */
	call<T = any>(input: object, before?: (reply: T) => void): T {
		if (this.closed) return null as T;
		const reply = JSON.parse(this.core.call(JSON.stringify(input))) as T;
		before?.(reply);
		this.drain();
		return reply;
	}

	/** The host's answer to io `id`. */
	answer(id: number, result: { ok: unknown } | { error: { code?: string; message: string } }) {
		this.call({ in: "answer", id, ...result });
	}

	/** A server core's request: the input gets the next request id; resolves with its `respond` item, rejects when the
	 *  core refuses the input itself. */
	request(input: object): Promise<Respond> {
		const id = ++this.next;
		return new Promise((ok, fail) => {
			this.waiting.set(id, ok);
			const reply = this.call<{ error?: string }>({ ...input, id });
			if (reply?.error) {
				this.waiting.delete(id);
				fail(new Error(reply.error));
			}
		});
	}

	/** Stop: nothing is called or answered any more. */
	close() {
		this.closed = true;
		this.waiting.clear();
	}

	private drain() {
		if (this.draining || this.closed) return;
		this.draining = true;
		try {
			for (;;) {
				const items = JSON.parse(this.core.takeIo()) as IoItem[];
				if (!items.length) break;
				for (const item of items) this.handle(item);
			}
		} finally {
			this.draining = false;
		}
	}

	private handle(item: IoItem) {
		if (item.io === "respond") {
			const r = item as Respond;
			const done = this.waiting.get(r.req);
			this.waiting.delete(r.req);
			done?.(r);
			return;
		}
		const h = this.handlers[item.io];
		if (!h) {
			if (item.id != null) this.answer(item.id, { error: { code: "unsupported", message: `unsupported io ${item.io}` } });
			else this.onWarn(`unhandled io ${item.io}`);
			return;
		}
		const out = h(item);
		if (out && typeof (out as Promise<unknown>).then === "function" && item.id != null) {
			const id = item.id;
			(out as Promise<unknown>).then(
				(ok) => this.answer(id, { ok: ok ?? null }),
				(e: any) => this.answer(id, { error: { code: String(e?.code ?? "host"), message: String(e?.message ?? e) } }),
			);
		}
	}
}
